import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import DeleteConfirmModal from "./delete-confirm-modal";
import { adminCouponDeleteAction, adminCouponsListAction } from "../Action/action";

function formatDate(value) {
  if (!value) return "—";
  const s = String(value);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

function discountLabel(row) {
  if (row.discount == null) return "—";
  const n = Number(row.discount);
  if (!Number.isFinite(n)) return "—";
  return String(row.type) === "amount" ? n.toFixed(2) : `${n}%`;
}

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

export default function Coupons() {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("All");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [reloadNonce, setReloadNonce] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, per_page: 10 });
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [query, status, perPage]);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const res = await adminCouponsListAction({
          page,
          per_page: perPage,
          search: query.trim() || undefined,
          status: status === "All" ? undefined : status.toLowerCase()
        });
        if (cancelled) return;
        if (!res?.success) {
          setRows([]);
          setMeta({ total: 0, page, per_page: perPage });
          setError(res?.message || res?.msg || "Failed to load coupons.");
          return;
        }
        setRows(Array.isArray(res.data) ? res.data : []);
        setMeta({
          total: Number(res?.meta?.total || 0),
          page: Number(res?.meta?.page || page),
          per_page: Number(res?.meta?.per_page || perPage)
        });
      } catch (e) {
        if (cancelled) return;
        setRows([]);
        setMeta({ total: 0, page, per_page: perPage });
        setError(e?.response?.data?.message || e?.message || "Failed to load coupons.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [page, perPage, query, status, reloadNonce]);

  const total = Number(meta?.total ?? 0);
  const totalPages = total > 0 ? Math.max(1, Math.ceil(total / perPage)) : 1;

  useEffect(() => {
    if (loading) return;
    if (page > totalPages) setPage(totalPages);
  }, [loading, page, totalPages]);

  const start = total > 0 && rows.length > 0 ? (page - 1) * perPage + 1 : 0;
  const end = total > 0 && rows.length > 0 ? start + rows.length - 1 : 0;

  const pageButtons = useMemo(() => {
    const max = 5;
    const safeTotal = Math.max(1, totalPages);
    const current = Math.min(Math.max(1, page), safeTotal);
    let s = Math.max(1, current - 2);
    let e = Math.min(safeTotal, s + max - 1);
    s = Math.max(1, e - max + 1);
    const arr = [];
    for (let i = s; i <= e; i += 1) arr.push(i);
    return arr;
  }, [page, totalPages]);

  const openDelete = (row) => {
    if (!row?.id) return;
    setDeleteRow(row);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteRow?.id) return;
    setDeleting(true);
    try {
      const res = await adminCouponDeleteAction(deleteRow.id);
      if (!res?.success) throw new Error(res?.message || res?.msg || "Delete failed");
      toast.success("Coupon deleted", { toastId: "coupon-delete" });
      setDeleteOpen(false);
      setDeleteRow(null);
      setReloadNonce((n) => n + 1);
    } catch (e) {
      toast.error(e?.message || "Delete failed", { toastId: "coupon-delete" });
    } finally {
      setDeleting(false);
    }
  };

  const clearAndReload = () => {
    setQuery("");
    setStatus("All");
    setPage(1);
    setError("");
    setReloadNonce((n) => n + 1);
  };

  return (
    <div className="content">
      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">
            <strong>Coupons unavailable.</strong> {error}
          </div>
        </div>
      ) : null}

      <div className="toolbar">
        <div className="flex items-center gap-3" style={{ flexWrap: "wrap" }}>
          <div className="search-box">
            <input
              type="text"
              placeholder="Search name or code…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="filter-group">
            {["All", "Active", "Inactive"].map((v) => (
              <button
                key={v}
                className={`filter-btn ${status === v ? "active" : ""}`}
                type="button"
                onClick={() => setStatus(v)}
              >
                {v}
              </button>
            ))}
          </div>
          <div className="filter-group" style={{ alignItems: "center" }}>
            <span className="small-label" style={{ marginRight: 8 }}>
              Per page
            </span>
            <select
              className="form-control"
              style={{ width: "auto", minWidth: 72, padding: "6px 10px" }}
              value={perPage}
              onChange={(e) => setPerPage(Number(e.target.value))}
              disabled={loading}
            >
              {PER_PAGE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <button className="btn btn-outline" type="button" onClick={clearAndReload} disabled={loading}>
            ↻ Clear
          </button>
        </div>
        <Link to="/coupons/new" className="btn btn-primary">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 5v14" />
            <path d="M5 12h14" />
          </svg>
          Add Coupon
        </Link>
      </div>

      <div className="card">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Discount</th>
                <th>For</th>
                <th>Status</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8}>
                    <div className="empty-state" style={{ padding: "28px 12px" }}>
                      Loading…
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <strong>{r.name || "—"}</strong>
                    </td>
                    <td>{r.code || "—"}</td>
                    <td>{discountLabel(r)}</td>
                    <td>
                      {Number(r.is_user_specific) ? (
                        <span className="badge badge-info">
                          Specific{r.assigned_user_count != null ? ` (${r.assigned_user_count})` : ""}
                        </span>
                      ) : (
                        <span className="badge badge-muted">All users</span>
                      )}
                    </td>
                    <td>
                      <span className={`badge ${Number(r.is_active) ? "badge-success" : "badge-muted"}`}>
                        {Number(r.is_active) ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>{formatDate(r.start_date)}</td>
                    <td>{formatDate(r.end_date)}</td>
                    <td>
                      <div className="actions-cell">
                        <Link to={`/coupons/${r.id}/edit`} className="action-link">
                          Edit
                        </Link>
                        <button
                          type="button"
                          className="action-link text-danger"
                          onClick={() => openDelete(r)}
                          style={{ background: "transparent", border: "none", padding: 0 }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
              {!loading && rows.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    <div className="empty-state">
                      <div className="empty-icon">🎟️</div>
                      No coupons found.
                    </div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <div className="pagination">
          <span>
            {total === 0
              ? "No coupons"
              : `Showing ${start}–${end} of ${total} Coupon${total === 1 ? "" : "s"} · Page ${page} of ${totalPages}`}
          </span>
          {total > 0 ? (
            <div className="pagination-pages">
              <button type="button" title="First page" onClick={() => setPage(1)} disabled={page <= 1 || loading}>
                {"<<"}
              </button>
              <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading}>
                ‹
              </button>
              {pageButtons.map((p) => (
                <button
                  key={p}
                  className={p === page ? "active" : ""}
                  type="button"
                  onClick={() => setPage(p)}
                  disabled={loading}
                >
                  {p}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
              >
                ›
              </button>
              <button
                type="button"
                title="Last page"
                onClick={() => setPage(totalPages)}
                disabled={page >= totalPages || loading}
              >
                {">>"}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <DeleteConfirmModal
        open={deleteOpen}
        itemType="coupon"
        itemName={deleteRow?.name || deleteRow?.code || "Coupon"}
        onCancel={() => {
          if (deleting) return;
          setDeleteOpen(false);
          setDeleteRow(null);
        }}
        onConfirm={confirmDelete}
        confirmDisabled={deleting}
      />
    </div>
  );
}
